import React, { useState, useEffect } from "react";
import "./FeedStyle.css";
import { observer } from "mobx-react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRss, faArrowLeft, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { getFeedListing } from "../../feedrequests";
import ReactGA from 'react-ga4';

function FeedPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
  }, [location]);

  const [initialized, setInitialized] = useState(false);
  const [listings, setListings] = useState([]);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const getListings = async feedUrl => {
    setError(null);
    setLoading(true);
    try {
      const response = await getFeedListing(feedUrl);
      setListings(response.data.items || []);
      setData(response.data.feed || {});
    } catch (ex) {
      setError("Failed to load the RSS feed. Please verify the URL and try again.");
    } finally {
      setLoading(false);
    }
  };

  const openLink = linkUrl => {
    window.open(linkUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (!initialized) {
      const feedUrl = searchParams.get("url"); 
      if (feedUrl) {
        getListings(feedUrl);
      }
      setInitialized(true);
    }
  }, [initialized, searchParams]);

  return (
    <div className="feed-page bg-light py-5 min-vh-100 mt-5">
      <div className="container feeds-container">
        <div className="row justify-content-center">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <Link to="/feed/feedhome" className="btn btn-outline-warning shadow-sm fw-bold">
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to Feeds
              </Link>
              <span className="badge bg-warning text-dark px-3 py-2 fw-bold text-uppercase">RSS Reader</span>
            </div>

            {data.title && (
              <div className="card shadow-sm border-0 rounded-3 mb-4 text-center p-4 bg-white">
                <div className="card-header bg-white border-bottom-0 pt-2 text-center">
                  <div className="text-warning mb-2 display-4">
                    {data.image ? (
                      <img
                        src={data.image}
                        alt="RSS Feed Logo"
                        style={{ maxHeight: "64px", maxWidth: "200px" }}
                        className="rounded"
                      />
                    ) : (
                      <FontAwesomeIcon icon={faRss} />
                    )}
                  </div>
                  <h3 className="fw-light my-2">{data.title}</h3>
                  {data.description && <p className="text-muted small mb-0">{data.description}</p>}
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-warning alert-dismissible fade show shadow-sm" role="alert">
                <strong>Notice:</strong> {error}
              </div>
            )}

            {loading && (
              <div className="card shadow-sm border-0 rounded-3 text-center py-5 px-3 bg-white">
                <div className="spinner-border text-warning mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted mb-0">Fetching feed items...</p>
              </div>
            )}

            {!loading && listings.length === 0 && !error && (
              <div className="card shadow-sm border-0 rounded-3 text-center py-5 px-3 bg-white">
                <div className="text-warning mb-2 display-6">
                  <FontAwesomeIcon icon={faRss} className="opacity-50" />
                </div>
                <p className="text-muted mb-0">No items available in this feed.</p>
              </div>
            )}

            {listings.map((l, i) => (
              <div className="card shadow-sm border-0 rounded-3 mb-3 bg-white" key={i}>
                <div className="card-body p-4">
                  <h5 className="fw-bold text-dark mb-2">{l.title}</h5>
                  {l.pubDate && (
                    <p className="text-muted small mb-2">
                      {new Date(l.pubDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                    </p>
                  )}
                  {l.description && (
                    <p className="text-muted small mb-3">
                      {l.description.replace(/<[^>]+>/g, '').slice(0, 300)}...
                    </p>
                  )}
                  <button
                    type="button"
                    className="btn btn-warning btn-sm shadow-sm fw-bold px-3"
                    onClick={() => openLink(l.link)}
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" /> Open Article
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default observer(FeedPage);